/**
 * Enhanced Print Service for Electron App
 * Handles printing, PDF export, Excel/CSV export, and document generation
 * Supports role-based permissions and multiple export strategies
 */

import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { enrichReportWithBusinessHeader } from '../utils/reportDocumentHeader';
import { buildReportDocumentHTML } from '../utils/reportDocumentBuilder';
import { getTranslation } from '../utils/constants';
import { readAppLanguage } from '../utils/readAppLanguage';
import { getDisplayLocale } from '../utils/formatSafety';
import { buildPrintAdsCssVariables } from '../utils/printAdsPalette';

class PrintService {
  constructor() {
    this.isElectron = typeof window !== 'undefined' && window.electronAPI;
    this.defaultPrintOptions = {
      silent: true,
      printBackground: true,
      color: true,
      margin: {
        marginType: 'none'
      },
      landscape: false,
      scaleFactor: 100,
      pagesPerSheet: 1,
      collate: false,
      copies: 1
    };
    
    this.defaultPdfOptions = {
      marginsType: 1,
      pageSize: 'A4',
      printBackground: true,
      printSelectionOnly: false,
      landscape: false
    };

    // Export-Strategien
    this.exportStrategies = {
      pdf: this.exportToPDF.bind(this),
      excel: this.exportToExcel.bind(this),
      csv: this.exportToCSV.bind(this),
      html: this.exportToHTML.bind(this)
    };
  }

  /**
   * Check if running in Electron environment
   * @returns {boolean}
   */
  isElectronEnvironment() {
    return this.isElectron;
  }

  /**
   * Open print preview window - this IS the preview
   * @param {string} html - HTML content to preview
   * @param {Object} options - Preview options
   * @returns {Promise<Object>} Preview result
   */
  async openPreview(html, options = {}) {
    if (!this.isElectron) {
      console.warn('Print functionality is only available in Electron environment');
      return {
        success: false,
        error: 'Print functionality is only available in Electron environment',
        message: 'Please run the application in Electron to use print functionality'
      };
    }

    try {
      // Validate HTML content
      if (!html || html.trim().length === 0) {
        throw new Error('HTML content is empty');
      }

      // Check HTML size and log method selection
      const htmlSize = Buffer.byteLength(html, 'utf8');
      const method = htmlSize > 50000 ? 'tempfile' : 'base64';
      console.log(`📄 [PREVIEW] HTML size: ${htmlSize} bytes, using method: ${method}`);

      // Open the preview window with robust method selection
      const previewResult = await window.electronAPI.openPreview(html);

      if (!previewResult.success) {
        return {
          success: false,
          error: previewResult.error,
          message: previewResult.message
        };
      }

      return {
        success: true,
        method: previewResult.method,
        htmlSize: htmlSize,
        message: `Print preview opened successfully using ${previewResult.method} method. Use Ctrl+P or Print button to print.`
      };
    } catch (error) {
      console.error('Open preview error:', error);
      return {
        success: false,
        error: error.message,
        message: 'Failed to open print preview'
      };
    }
  }

  /**
   * Print from currently focused window (the preview window)
   * @param {Object} options - Print options
   * @returns {Promise<Object>} Print result
   */
  async printFromFocused(options = {}) {
    if (!this.isElectron) {
      console.warn('Print functionality is only available in Electron environment');
      return {
        ok: false,
        error: 'Print functionality is only available in Electron environment',
        message: 'Please run the application in Electron to use print functionality'
      };
    }

    try {
      const result = await window.electronAPI.printFromFocused(options);
      return {
        ok: result.ok,
        message: result.message,
        error: result.error,
        reason: result.reason
      };
    } catch (error) {
      console.error('Print from focused error:', error);
      return {
        ok: false,
        error: error.message,
        message: 'Failed to print from focused window'
      };
    }
  }

  /**
   * Print HTML — silent to saved/default printer by default (no system dialog).
   * Pass `forceDialog: true` only when a visible dialog is required (e.g. Save as PDF).
   * Pass `exportPdf: true` for Electron HTML→PDF export.
   */
  async printWithDialog(html, options = {}) {
    if (!html || String(html).trim().length === 0) {
      return {
        success: false,
        error: 'HTML content is empty',
        message: 'HTML content is empty'
      };
    }

    if (options.exportPdf === true && this.isElectron && window.electronAPI?.printWithDialog) {
      try {
        return await window.electronAPI.printWithDialog(html, options);
      } catch (error) {
        console.error('PDF export via print channel failed:', error);
        return { success: false, error: error.message };
      }
    }

    // Default: silent + fast (POS receipts, invoices, reports).
    if (options.forceDialog !== true) {
      try {
        const { printSilentFast } = await import('../utils/printerUtils');
        const kind =
          options.kind === 'thermal'
            ? 'thermal'
            : options.kind === 'label-sheet'
              ? 'label-sheet'
              : 'document';
        return await printSilentFast(html, {
          kind,
          role: options.role,
          deviceName: options.deviceName,
          margins: options.margins ?? { marginType: 'none' },
          timeoutMs: options.timeoutMs,
          retries: options.retries,
          imageWaitMs: options.imageWaitMs,
          pageWidthMm: options.pageWidthMm,
          pageHeightMm: options.pageHeightMm
        });
      } catch (error) {
        console.error('Silent print error:', error);
        return { success: false, error: error.message };
      }
    }

    if (!this.isElectron) {
      if (typeof window !== 'undefined' && html) {
        const { printHtmlInBrowser } = await import('../utils/printHtmlDocument');
        return printHtmlInBrowser(html, { windowFeatures: 'width=900,height=700' });
      }
      return {
        success: false,
        error: 'Print functionality is only available in Electron environment',
        message: 'Please run the application in Electron to use print functionality'
      };
    }

    try {
      const result = await window.electronAPI.printWithDialog(html, {
        ...options,
        forceDialog: true,
        silent: false,
        printBackground: options.printBackground !== false
      });
      return result;
    } catch (error) {
      console.error('Print dialog error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * List available printers
   * @returns {Promise<Object>} List of available printers
   */
  async listPrinters() {
    if (!this.isElectron) {
      console.warn('Print functionality is only available in Electron environment');
      return {
        success: false,
        error: 'Print functionality is only available in Electron environment',
        printers: []
      };
    }

    try {
      const result = await window.electronAPI.listPrinters();
      return {
        success: result.success,
        printers: result.printers || [],
        error: result.error
      };
    } catch (error) {
      console.error('List printers error:', error);
      return {
        success: false,
        printers: [],
        error: error.message
      };
    }
  }

  /**
   * Silent print HTML content to specific device (no system dialog).
   * Main process owns timeout/retry; renderer only applies a safety ceiling.
   * @param {string} html
   * @param {string} deviceName
   * @param {string|object} [margins='default']
   * @param {{ kind?: 'thermal'|'document'|'label-sheet', timeoutMs?: number, retries?: number, pageWidthMm?: number, pageHeightMm?: number }} [options]
   */
  async printSilentHTML(html, deviceName, margins = 'default', options = {}) {
    if (!this.isElectron) {
      console.warn('⚠️ [PRINT SERVICE] Not in Electron environment');
      return {
        success: false,
        error: 'Print functionality is only available in Electron environment',
        message: 'Please run the application in Electron to use print functionality'
      };
    }

    if (!deviceName) {
      console.error('❌ [PRINT SERVICE] Device name is missing');
      return {
        success: false,
        error: 'Device name is required for silent printing',
        message: 'Please provide a printer device name'
      };
    }

    const kind =
      options?.kind === 'document'
        ? 'document'
        : options?.kind === 'label-sheet'
          ? 'label-sheet'
          : 'thermal';
    const timeoutMs = Math.max(
      4000,
      Number(options?.timeoutMs) ||
        (kind === 'document' || kind === 'label-sheet' ? 12000 : 8000)
    );
    // Safety ceiling above main-process timeout + retry budget
    const safetyMs = timeoutMs * (1 + Math.max(0, Number(options?.retries) || 1)) + 2500;

    try {
      const printPromise = window.electronAPI.printSilentHTML(
        html,
        deviceName,
        margins,
        { ...options, kind, timeoutMs }
      );

      let safetyTimer;
      const safetyPromise = new Promise((_, reject) => {
        safetyTimer = setTimeout(
          () => reject(new Error(`Print safety timeout after ${safetyMs}ms`)),
          safetyMs
        );
      });

      const result = await Promise.race([printPromise, safetyPromise]).finally(() => {
        clearTimeout(safetyTimer);
      });

      return {
        success: Boolean(result?.success),
        message: result?.message,
        error: result?.error || null,
        deviceName: result?.deviceName || deviceName,
        attempts: result?.attempts
      };
    } catch (error) {
      console.error('Print error:', error.message);
      return {
        success: false,
        error: error.message,
        message: 'Failed to perform silent print'
      };
    }
  }

  /**
   * Silent print HTML content to specific device (legacy method)
   * @param {string} html - HTML content to print
   * @param {string} deviceName - Printer device name
   * @param {Object} options - Print options
   * @returns {Promise<Object>} Print result
   */
  async silentPrint(html, deviceName, options = {}) {
    const { margins = 'default', ...printOptions } = options || {};
    return await this.printSilentHTML(html, deviceName, margins, printOptions);
  }

  /**
   * Generate PDF from HTML content
   * @param {string} html - HTML content to convert to PDF
   * @param {Object} pdfOptions - PDF generation options
   * @returns {Promise<Object>} PDF result with buffer
   */
  async generatePDF(html, pdfOptions = {}) {
    if (!this.isElectron) {
      console.warn('PDF generation is only available in Electron environment');
      return {
        success: false,
        error: 'PDF generation is only available in Electron environment',
        message: 'Please run the application in Electron to use PDF generation',
        pdfBuffer: null
      };
    }

    try {
      const finalPdfOptions = {
        ...this.defaultPdfOptions,
        ...pdfOptions
      };

      const result = await window.electronAPI.printToPDF({
        html,
        pdfOptions: finalPdfOptions
      });

      return {
        success: result.success,
        pdfBuffer: result.pdfBuffer,
        message: result.message,
        error: result.error
      };
    } catch (error) {
      console.error('PDF generation error:', error);
      return {
        success: false,
        error: error.message,
        message: 'Failed to generate PDF',
        pdfBuffer: null
      };
    }
  }

  /**
   * Print content from a route (same origin - can access localStorage)
   * @param {string} route - Route path (e.g., '/print?type=invoice&id=123')
   * @param {Object} options - Print options
   * @returns {Promise<Object>} Print result
   */
  async printFromRoute(route, options = {}) {
    if (!this.isElectron) {
      console.warn('Route-based printing is only available in Electron environment');
      return {
        success: false,
        error: 'Route-based printing is only available in Electron environment',
        message: 'Please run the application in Electron to use route-based printing'
      };
    }

    try {
      // Create a temporary window to load the route
      const currentUrl = window.location.href;
      const baseUrl = currentUrl.split('#')[0];
      const routeUrl = `${baseUrl}#${route}`;

      // Open route in new window to capture its content
      const routeWindow = window.open(routeUrl, '_blank', 'width=800,height=600');
      
      if (!routeWindow) {
        throw new Error('Failed to open route window');
      }

      // Wait for route to load
      await new Promise((resolve, reject) => {
        const checkLoaded = () => {
          try {
            if (routeWindow.document.readyState === 'complete') {
              resolve();
            } else {
              setTimeout(checkLoaded, 100);
            }
          } catch (error) {
            reject(error);
          }
        };
        checkLoaded();
      });

      // Get the HTML content from the route
      const html = routeWindow.document.documentElement.outerHTML;
      routeWindow.close();

      // Print the content
      return await this.printWithDialog(html, options);
    } catch (error) {
      console.error('Route-based print error:', error);
      return {
        success: false,
        error: error.message,
        message: 'Failed to print from route'
      };
    }
  }

  /**
   * Get available printers (if supported by Electron)
   * @returns {Promise<Array>} List of available printers
   */
  async getAvailablePrinters() {
    if (!this.isElectron) {
      return [];
    }

    // This would need to be implemented in the main process
    // For now, return empty array as Electron doesn't expose this directly
    return [];
  }

  /**
   * Validate HTML content
   * @param {string} html - HTML content to validate
   * @returns {boolean} Whether HTML is valid
   */
  validateHTML(html) {
    if (!html || typeof html !== 'string') {
      return false;
    }

    // Basic HTML validation
    const hasOpeningTag = html.includes('<');
    const hasClosingTag = html.includes('>');
    
    return hasOpeningTag && hasClosingTag;
  }

  /**
   * Sanitize HTML content for printing
   * @param {string} html - HTML content to sanitize
   * @returns {string} Sanitized HTML
   */
  sanitizeHTML(html) {
    if (!this.validateHTML(html)) {
      throw new Error('Invalid HTML content provided');
    }

    // Basic sanitization - remove script tags and dangerous attributes
    let sanitized = html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/on\w+="[^"]*"/gi, '')
      .replace(/javascript:/gi, '');

    // Ensure proper HTML structure
    if (!sanitized.includes('<html')) {
      sanitized = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Print Document</title></head><body>${sanitized}</body></html>`;
    }

    return sanitized;
  }

  /**
   * Create a simple HTML template for printing
   * @param {Object} data - Data to include in template
   * @param {string} template - HTML template string
   * @returns {string} Rendered HTML
   */
  createHTMLTemplate(data, template) {
    try {
      // Simple template replacement
      let rendered = template;
      
      Object.keys(data).forEach(key => {
        const placeholder = `{{${key}}}`;
        const value = data[key] || '';
        rendered = rendered.replace(new RegExp(placeholder, 'g'), value);
      });

      return this.sanitizeHTML(rendered);
    } catch (error) {
      console.error('Template rendering error:', error);
      throw new Error('Failed to render HTML template');
    }
  }

  // ==================== NEUE EXPORT-METHODEN ====================

  /**
   * Export data to PDF using jsPDF
   * @param {Object} reportData - Data to export
   * @param {Object} options - PDF options
   * @returns {Promise<Object>} Export result
   */
  async exportToPDF(reportData, options = {}) {
    try {
      const {
        title = reportData.title || 'Report',
        filename = 'report.pdf',
        orientation = 'portrait',
        format = 'a4',
        includeCharts = false
      } = options;

      const doc = new jsPDF({
        orientation,
        unit: 'mm',
        format
      });

      // Add title
      doc.setFontSize(20);
      doc.text(title || reportData.title || 'Report', 20, 20);

      // Add data table if provided
      if (reportData.tableData && reportData.tableData.length > 0) {
        doc.autoTable({
          head: [reportData.headers || Object.keys(reportData.tableData[0])],
          body: reportData.tableData.map(row => 
            Object.values(row).map(value => String(value))
          ),
          startY: 30,
          styles: { fontSize: 10 },
          headStyles: { fillColor: [66, 139, 202] }
        });
      }

      // Add summary if provided
      if (reportData.summary) {
        const finalY = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(12);
        doc.text('Summary:', 20, finalY);
        
        Object.entries(reportData.summary).forEach(([key, value], index) => {
          doc.setFontSize(10);
          doc.text(`${key}: ${value}`, 20, finalY + 10 + (index * 5));
        });
      }

      // Generate PDF buffer
      const pdfBuffer = doc.output('arraybuffer');
      
      if (this.isElectron) {
        // Save via Electron
        const result = await window.electronAPI.printToPDF({
          pdfBuffer,
          filename
        });
        return result;
      } else {
        // Download in browser
        const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        
        return { success: true, filename };
      }
    } catch (error) {
      console.error('PDF export error:', error);
      throw new Error('Failed to export PDF');
    }
  }

  /**
   * Export data to Excel using XLSX
   * @param {Object} reportData - Data to export
   * @param {Object} options - Excel options
   * @returns {Promise<Object>} Export result
   */
  async exportToExcel(reportData, options = {}) {
    try {
      const {
        filename = 'report.xlsx',
        sheetName = 'Report',
        includeCharts = false
      } = options;

      const workbook = XLSX.utils.book_new();

      // Add main data sheet
      if (reportData.tableData && reportData.tableData.length > 0) {
        const worksheet = XLSX.utils.json_to_sheet(reportData.tableData);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      }

      // Add summary sheet if provided
      if (reportData.summary) {
        const summaryData = Object.entries(reportData.summary).map(([key, value]) => ({
          Metric: key,
          Value: value
        }));
        const summarySheet = XLSX.utils.json_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
      }

      // Generate Excel buffer
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      
      if (this.isElectron) {
        // Save via Electron file dialog
        const result = await window.electronAPI.saveFile();
        if (result.filePath) {
          // Write file using Node.js fs (would need to be implemented in main process)
          return { success: true, filename: result.filePath };
        }
      } else {
        // Download in browser
        const blob = new Blob([excelBuffer], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        
        return { success: true, filename };
      }
    } catch (error) {
      console.error('Excel export error:', error);
      throw new Error('Failed to export Excel');
    }
  }

  /**
   * Export data to CSV
   * @param {Object} reportData - Data to export
   * @param {Object} options - CSV options
   * @returns {Promise<Object>} Export result
   */
  async exportToCSV(reportData, options = {}) {
    try {
      const {
        filename = 'report.csv',
        delimiter = ',',
        includeHeaders = true
      } = options;

      if (!reportData.tableData || reportData.tableData.length === 0) {
        throw new Error('No data to export');
      }

      let csvContent = '';

      // Add headers
      if (includeHeaders) {
        const headers = reportData.headers || Object.keys(reportData.tableData[0]);
        csvContent += headers.join(delimiter) + '\n';
      }

      // Add data rows
      reportData.tableData.forEach(row => {
        const values = Object.values(row).map(value => 
          typeof value === 'string' && value.includes(delimiter) 
            ? `"${value}"` 
            : value
        );
        csvContent += values.join(delimiter) + '\n';
      });

      // Convert to blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);

      return { success: true, filename };
    } catch (error) {
      console.error('CSV export error:', error);
      throw new Error('Failed to export CSV');
    }
  }

  /**
   * Export data to HTML
   * @param {Object} reportData - Data to export
   * @param {Object} options - HTML options
   * @returns {Promise<Object>} Export result
   */
  async exportToHTML(reportData, options = {}) {
    try {
      const {
        filename = 'report.html',
        title = reportData.title || 'Report',
        includeStyles = true
      } = options;

      const reportTitle = title || reportData.title || 'Report';

      let htmlContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${reportTitle}</title>`;

      if (includeStyles) {
        htmlContent += `
  <style>
    ${buildPrintAdsCssVariables()}
    html, body { color: #111827 !important; background: #ffffff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: Arial, sans-serif; margin: 20px; color: var(--print-text, #111827); background: var(--print-surface, #ffffff); }
    table { border-collapse: collapse; width: 100%; margin: 20px 0; }
    th, td { border: 1px solid var(--print-secondary-200); padding: 8px; text-align: left; }
    th { background-color: var(--print-secondary-100); }
    .summary { background-color: var(--print-secondary-100); padding: 15px; margin: 20px 0; }
  </style>`;
      }

      htmlContent += `
</head>
<body>
  <h1>${reportTitle}</h1>
  <p>Generated on: ${new Date().toLocaleString()}</p>`;

      // Add summary if provided
      if (reportData.summary) {
        htmlContent += `
  <div class="summary">
    <h2>Summary</h2>`;
        Object.entries(reportData.summary).forEach(([key, value]) => {
          htmlContent += `<p><strong>${key}:</strong> ${value}</p>`;
        });
        htmlContent += `</div>`;
      }

      // Add data table
      if (reportData.tableData && reportData.tableData.length > 0) {
        htmlContent += `
  <table>
    <thead>
      <tr>`;
        const headers = reportData.headers || Object.keys(reportData.tableData[0]);
        headers.forEach(header => {
          htmlContent += `<th>${header}</th>`;
        });
        htmlContent += `
      </tr>
    </thead>
    <tbody>`;
        
        reportData.tableData.forEach(row => {
          htmlContent += `<tr>`;
          Object.values(row).forEach(value => {
            htmlContent += `<td>${value}</td>`;
          });
          htmlContent += `</tr>`;
        });
        
        htmlContent += `
    </tbody>
  </table>`;
      }

      htmlContent += `
</body>
</html>`;

      // Save HTML file
      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);

      return { success: true, filename };
    } catch (error) {
      console.error('HTML export error:', error);
      throw new Error('Failed to export HTML');
    }
  }

  /**
   * Universal export method - routes to appropriate strategy
   * @param {Object} reportData - Data to export
   * @param {string} format - Export format (pdf, excel, csv, html)
   * @param {Object} options - Export options
   * @returns {Promise<Object>} Export result
   */
  async exportReport(reportData, format = 'pdf', options = {}) {
    const strategy = this.exportStrategies[format.toLowerCase()];
    if (!strategy) {
      throw new Error(`Unsupported export format: ${format}`);
    }
    
    return await strategy(reportData, options);
  }

  /**
   * Print legacy inline receipt HTML via role-aware silent print (receipt role).
   * Prefer printFacade.printReceipt for POS thermal templates.
   * @param {Object} saleData - Sale data
   * @returns {Promise<Object>} Print result
   */
  async printReceipt(saleData) {
    const html = this.generateReceiptHTML(saleData);
    const { printSilentFast } = await import('../utils/printerUtils');
    const { PRINT_ROLE } = await import('../domain/print');
    return printSilentFast(html, {
      kind: 'thermal',
      role: PRINT_ROLE.RECEIPT,
      margins: { marginType: 'printableArea' }
    });
  }

  /**
   * Print legacy inline invoice HTML via role-aware silent print (document role).
   * Prefer printFacade.printInvoice for full facture layouts.
   * @param {Object} invoiceData - Invoice data
   * @returns {Promise<Object>} Print result
   */
  async printInvoice(invoiceData) {
    const html = this.generateInvoiceHTML(invoiceData);
    const { printSilentFast } = await import('../utils/printerUtils');
    const { PRINT_ROLE } = await import('../domain/print');
    return printSilentFast(html, {
      kind: 'document',
      role: PRINT_ROLE.DOCUMENT,
      margins: { marginType: 'printableArea' }
    });
  }

  /**
   * Print report with role-based permissions
   * @param {Object} reportData - Report data
   * @returns {Promise<Object>} Print result
   */
  async printReport(reportData) {
    try {
      const lang = reportData.exportLang || reportData.currentLanguage ||
        (reportData.isRTL ? 'ar' : 'fr');
      const enriched = await enrichReportWithBusinessHeader({
        ...reportData,
        exportLang: lang
      });
      const html = buildReportDocumentHTML(enriched, { lang: lang === 'ar' ? 'ar' : 'fr' });

      return await this.printWithDialog(html, {
        kind: 'document',
        printBackground: true,
        margins: { marginType: 'none' },
        timeoutMs: 10000,
        retries: 1
      });
    } catch (error) {
      console.error('Print report error:', error);
      return {
        success: false,
        error: error.message,
        message: 'Failed to print report'
      };
    }
  }

  /**
   * @param {Object} saleData
   * @param {string} [language]
   */
  generateReceiptHTML(saleData, language) {
    const lang = language || saleData?.language || saleData?.currentLanguage || readAppLanguage();
    const l = (key) => getTranslation(`printServicePage.${key}`, lang);
    const locale = getDisplayLocale(lang === 'ar');
    const dateStr = new Date().toLocaleString(locale);
    const emptyDash = '—';

    return `<!DOCTYPE html>
<html lang="${lang === 'ar' ? 'ar' : 'fr'}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${l('receiptTitle')}</title>
  <style>
    ${buildPrintAdsCssVariables()}
    * { box-sizing: border-box; }
    html, body { color: #111827 !important; background: #ffffff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body {
      font-family: 'Courier New', monospace;
      margin: 0;
      padding: 20px;
      background: var(--print-surface, #ffffff);
      color: var(--print-text, #111827);
      font-size: 14px;
      line-height: 1.4;
    }
    .receipt {
      width: 300px;
      margin: 0 auto;
      border: 1px solid var(--print-text);
      padding: 20px;
      background: var(--print-surface);
    }
    .header {
      text-align: center;
      margin-bottom: 20px;
      border-bottom: 1px solid var(--print-text);
      padding-bottom: 10px;
    }
    .header h2 {
      margin: 0;
      font-size: 18px;
      font-weight: bold;
    }
    .info {
      margin: 10px 0;
    }
    .item {
      display: flex;
      justify-content: space-between;
      margin: 5px 0;
      padding: 2px 0;
    }
    .total {
      display: flex;
      justify-content: space-between;
      font-weight: bold;
      border-top: 2px solid var(--print-text);
      padding-top: 10px;
      margin-top: 15px;
      font-size: 16px;
    }
    hr {
      border: none;
      border-top: 1px solid var(--print-text);
      margin: 10px 0;
    }
  </style>
</head>
<body>
  <div class="receipt">
    <div class="header">
      <h2>${l('receiptTitle')}</h2>
    </div>
    <div class="info">
      <p><strong>${l('date')}:</strong> ${dateStr}</p>
      <p><strong>${l('saleId')}:</strong> ${saleData.id || emptyDash}</p>
    </div>
    <hr>
    ${saleData.items?.map(item => `
      <div class="item">
        <span>${item.name || l('item')}</span>
        <span>${item.price || '0.00'}</span>
      </div>
    `).join('') || `<div class="item"><span>${l('noItems')}</span><span>0.00</span></div>`}
    <div class="total">
      <span>${l('total')}:</span>
      <span>${saleData.total || '0.00'}</span>
    </div>
  </div>
</body>
</html>`;
  }

  /**
   * @param {Object} invoiceData
   * @param {string} [language]
   */
  generateInvoiceHTML(invoiceData, language) {
    const lang = language || invoiceData?.language || invoiceData?.currentLanguage || readAppLanguage();
    const l = (key) => getTranslation(`printServicePage.${key}`, lang);
    const locale = getDisplayLocale(lang === 'ar');
    const dateStr = new Date().toLocaleString(locale);
    const emptyDash = '—';

    return `<!DOCTYPE html>
<html lang="${lang === 'ar' ? 'ar' : 'fr'}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${l('invoiceTitle')}</title>
  <style>
    ${buildPrintAdsCssVariables()}
    html, body { color: #111827 !important; background: #ffffff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body {
      font-family: Arial, sans-serif;
      margin: 0;
      padding: 20px;
      background: var(--print-surface, #ffffff);
      color: var(--print-text, #111827);
    }
    .invoice {
      max-width: 800px;
      margin: 0 auto;
      border: 1px solid var(--print-secondary-300);
      padding: 30px;
    }
    .header {
      text-align: center;
      margin-bottom: 30px;
    }
    .info-section {
      display: flex;
      justify-content: space-between;
      margin-bottom: 30px;
    }
    .info-box h3 {
      margin-top: 0;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
    }
    th, td {
      border: 1px solid var(--print-secondary-200);
      padding: 8px;
      text-align: left;
    }
    th {
      background-color: var(--print-secondary-100);
    }
    .totals {
      text-align: right;
    }
    .totals p {
      margin: 5px 0;
    }
  </style>
</head>
<body>
  <div class="invoice">
    <div class="header">
      <h1>${l('invoiceTitle')}</h1>
    </div>
    <div class="info-section">
      <div>
        <h3>${l('from')}:</h3>
        <p>${invoiceData.business?.name || l('businessName')}</p>
        <p>${invoiceData.business?.address || l('address')}</p>
      </div>
      <div>
        <h3>${l('invoiceDetails')}:</h3>
        <p><strong>${l('invoiceNumber')}:</strong> ${invoiceData.invoiceNumber || emptyDash}</p>
        <p><strong>${l('date')}:</strong> ${dateStr}</p>
        <p><strong>${l('dueDate')}:</strong> ${invoiceData.dueDate || emptyDash}</p>
      </div>
    </div>
    <table>
      <thead>
        <tr>
          <th>${l('colItem')}</th>
          <th>${l('colQuantity')}</th>
          <th>${l('colPrice')}</th>
          <th>${l('colTotal')}</th>
        </tr>
      </thead>
      <tbody>
        ${invoiceData.items?.map(item => `
          <tr>
            <td>${item.name}</td>
            <td>${item.quantity}</td>
            <td>${item.price}</td>
            <td>${item.total}</td>
          </tr>
        `).join('') || ''}
      </tbody>
    </table>
    <div class="totals">
      <p><strong>${l('subtotal')}:</strong> ${invoiceData.subtotal || '0.00'}</p>
      <p><strong>${l('tax')}:</strong> ${invoiceData.tax || '0.00'}</p>
      <p><strong>${l('totalLabel')}:</strong> ${invoiceData.total || '0.00'}</p>
    </div>
  </div>
</body>
</html>`;
  }

  /**
   * Generate report HTML template (delegates to unified report document builder).
   * @param {Object} reportData - Report data
   * @returns {string} HTML content
   */
  generateReportHTML(reportData) {
    const lang = reportData.exportLang || reportData.currentLanguage ||
      (reportData.isRTL ? 'ar' : 'fr');
    return buildReportDocumentHTML(reportData, { lang: lang === 'ar' ? 'ar' : 'fr' });
  }
}

// Create singleton instance
const printService = new PrintService();

export default printService;
